import { useState, useEffect } from 'react'
import { Head, router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import SenderFilters from '@/components/SenderFilters'
import SenderCard from '@/components/SenderCard'
import AddSenderModal from '@/components/AddSenderModal'
import EmptyState from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Plus } from 'lucide-react'
import { Sender } from '@/types/sender'

interface IndexProps {
  account: {
    id: number
    name: string
  }
  senders: Sender[]
  pagination: {
    page: number
    perPage: number
    totalCount: number
    totalPages: number
  }
  filters: {
    query: string
    sortBy: string
    blocked: boolean
    unblocked: boolean
    seen30d: boolean
    seen3mo: boolean
    seen6mo: boolean
    seen1yr: boolean
    seenEq1: boolean
    seenGt1: boolean
    seenGt10: boolean
    seenGt30: boolean
  }
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
    timezone?: string
  }
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
}

export default function Index({
  account,
  senders: initialSenders,
  pagination,
  filters,
  currentUser,
  gmailConnection,
}: IndexProps) {
  const [senders, setSenders] = useState<Sender[]>(initialSenders)
  const [showModal, setShowModal] = useState(false)

  // Sync senders when props change
  useEffect(() => {
    setSenders(initialSenders)
  }, [initialSenders])

  const handleToggleBlock = async (senderId: number, currentStatus: boolean) => {
    // Optimistically update the UI
    setSenders((prevSenders) =>
      prevSenders.map((sender) =>
        sender.id === senderId ? { ...sender, blocked: !currentStatus } : sender
      )
    )

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

      const response = await fetch(`/accounts/${account.id}/senders/${senderId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify({
          blocked: !currentStatus,
        }),
      })

      if (!response.ok) {
        // Revert on error
        setSenders((prevSenders) =>
          prevSenders.map((sender) =>
            sender.id === senderId ? { ...sender, blocked: currentStatus } : sender
          )
        )
      }
    } catch (error) {
      console.error('Failed to toggle sender block status:', error)
      // Revert on error
      setSenders((prevSenders) =>
        prevSenders.map((sender) =>
          sender.id === senderId ? { ...sender, blocked: currentStatus } : sender
        )
      )
    }
  }

  const handleModalSuccess = () => {
    // Reload the page to show the newly added sender
    router.reload({ only: ['senders', 'pagination'] })
  }

  const totalSenders = pagination.totalCount
  const hasResults = senders.length > 0
  const hasSearchQuery = filters.query.length > 0
  const hasFilters =
    filters.blocked ||
    filters.unblocked ||
    filters.seen30d ||
    filters.seen3mo ||
    filters.seen6mo ||
    filters.seen1yr ||
    filters.seenEq1 ||
    filters.seenGt1 ||
    filters.seenGt10 ||
    filters.seenGt30

  return (
    <>
      <Head title="Senders" />
      <DashboardLayout
        currentUser={currentUser}
        gmailConnection={gmailConnection}
        account={account}
        activeTab="senders"
      >
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-blue-950 dark:text-white mb-2">
              Senders
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              {totalSenders === 0
                ? 'No senders'
                : `${totalSenders} ${totalSenders === 1 ? 'sender' : 'senders'}`}
            </p>
          </div>
          <Button onClick={() => setShowModal(true)} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Add Sender
          </Button>
        </div>

        {/* Filters */}
        <SenderFilters
          query={filters.query}
          sortBy={filters.sortBy}
          blocked={filters.blocked}
          unblocked={filters.unblocked}
          seen30d={filters.seen30d}
          seen3mo={filters.seen3mo}
          seen6mo={filters.seen6mo}
          seen1yr={filters.seen1yr}
          seenEq1={filters.seenEq1}
          seenGt1={filters.seenGt1}
          seenGt10={filters.seenGt10}
          seenGt30={filters.seenGt30}
          accountId={account.id}
        />

        {/* Senders List */}
        {hasResults ? (
          <Card className="rounded-lg overflow-hidden">
            <CardContent className="p-0">
              {senders.map((sender, index) => (
                <SenderCard
                  key={sender.id}
                  sender={sender}
                  onToggleBlock={handleToggleBlock}
                  isFirst={index === 0}
                  isLast={index === senders.length - 1}
                  timezone={currentUser?.timezone || 'America/New_York'}
                />
              ))}
            </CardContent>
          </Card>
        ) : (
          <EmptyState
            title="No senders found"
            description={
              hasSearchQuery || hasFilters
                ? 'Try adjusting your search query or filters'
                : 'Senders will appear here as emails are processed. You can also add senders manually.'
            }
          />
        )}

        {/* Pagination Info */}
        {hasResults && pagination.totalPages > 1 && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing page {pagination.page} of {pagination.totalPages}
            </p>
          </div>
        )}

        {/* Add Sender Modal */}
        {showModal && (
          <AddSenderModal
            accountId={account.id}
            onClose={() => setShowModal(false)}
            onSuccess={handleModalSuccess}
          />
        )}
      </div>
    </DashboardLayout>
    </>
  )
}
